import { formatOeDisplay } from '@/utils/partDisplay';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component to load cart item image
const CartItemImage = ({ image, alt }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (image) {
      axios.get(`${API}/image/${image}`)
        .then(res => {
          if (res.data.data_url) {
            setImageUrl(res.data.data_url);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [image]);

  if (loading) {
    return <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">Loading...</div>;
  }

  if (imageUrl) {
    return <img src={imageUrl} alt={alt} className="w-full h-full object-contain" />;
  }

  return <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">No Image</div>;
};

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getTotalMRP, getTotalDiscountedPrice, clearCart } = useCart();
  const totalMRP = getTotalMRP();
  const totalDiscounted = getTotalDiscountedPrice();

  // Refresh each cart item's MOQ from the backend on mount. Older cart
  // entries (saved before the MOQ rollout) don't carry a moq field, so
  // we fetch the latest deployed value and snap each line's quantity
  // up to a valid MOQ multiple. Runs once per cart-page open.
  useEffect(() => {
    if (!cart || cart.length === 0) return;
    const jps = cart.map(c => c.jp_part_no).filter(Boolean);
    if (jps.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        // Hit the public parts endpoint for each cart line in parallel;
        // small batches keep things simple without adding a new bulk API.
        const results = await Promise.all(
          jps.map(jp => axios.get(`${API}/parts/${encodeURIComponent(jp)}`)
            .then(r => ({ jp, moq: r.data?.moq, coupon_amount: r.data?.coupon_amount }))
            .catch(() => null))
        );
        if (cancelled) return;
        results.forEach(r => {
          if (!r) return;
          const moq = Math.max(1, parseInt(r.moq, 10) || 1);
          const item = cart.find(c => c.jp_part_no === r.jp);
          if (!item) return;
          const currentQty = item.quantity || 1;
          const needsBump = currentQty < moq || (currentQty % moq !== 0);
          // Update the line: bake the latest moq onto it AND snap qty.
          const newQty = needsBump ? Math.ceil(currentQty / moq) * moq : currentQty;
          if (needsBump) {
            toast.warning(`${r.jp}: MOQ is ${moq}. Quantity updated from ${currentQty} → ${newQty}.`);
          }
          // updateQuantity itself re-snaps using item.moq, so we
          // overwrite the line in localStorage with the fresh MOQ first
          // via a direct sanitization pass. The cleanest minimal path
          // is to set the quantity here — moq is rehydrated on the next
          // sanitize sweep when the user changes anything.
          if (newQty !== currentQty || (item.moq || 1) !== moq) {
            // Patch through context's updateQuantity (snaps using
            // item.moq which is still 1 → no-op). To actually persist
            // moq onto the item we patch localStorage directly here.
            try {
              const saved = JSON.parse(localStorage.getItem('sparePartsCart') || '[]');
              const next = saved.map(s => s.jp_part_no === r.jp
                ? { ...s, moq, coupon_amount: r.coupon_amount ?? s.coupon_amount, quantity: newQty }
                : s);
              localStorage.setItem('sparePartsCart', JSON.stringify(next));
            } catch (_) { /* ignore */ }
            updateQuantity(r.jp, newQty);
          }
        });
      } catch (_) { /* network errors are non-fatal */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQuantityChange = (jp_part_no, newQuantity, moq = 1) => {
    if (newQuantity < (moq || 1)) return;
    updateQuantity(jp_part_no, newQuantity);
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-white py-20">
        <div className="container mx-auto px-4 text-center" data-testid="empty-cart">
          <ShoppingBag className="w-20 h-20 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-heading font-bold mb-2 text-gray-900">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Add some parts to get started</p>
          <Button
            onClick={() => navigate('/catalog')}
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
            data-testid="browse-catalog-button"
          >
            Browse Catalog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: '#ffffff' }}>
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-heading font-bold" data-testid="cart-title">
                Shopping Cart
              </h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  clearCart();
                  toast.success('Cart cleared');
                }}
                data-testid="clear-cart-button"
              >
                Clear All
              </Button>
            </div>

            <div className="space-y-4" data-testid="cart-items">
              {cart.map((item) => (
                <div
                  key={item.jp_part_no}
                  className="bg-white border border-border rounded-sm p-4 flex gap-4"
                  data-testid={`cart-item-${item.jp_part_no}`}
                >
                  {/* Image */}
                  <div className="w-24 h-24 flex-shrink-0 bg-gray-50 rounded-sm overflow-hidden flex items-center justify-center">
                    <CartItemImage image={item.image} alt={item.part_name} />
                  </div>

                  {/* Details */}
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1 text-gray-900">{item.part_name}</h3>
                    <p className="text-sm text-gray-700 part-number mb-1">
                      JP: {item.new_jp_part_no || item.jp_part_no}
                      {item.new_jp_part_no && item.new_jp_part_no !== item.jp_part_no && (
                        <span className="text-xs text-gray-400 ml-1">({item.jp_part_no})</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-700 part-number">
                      {formatOeDisplay(item.oe_part_no, true)}
                    </p>
                    <div className="mt-2">
                      <p className="text-sm text-gray-700">
                        MRP: ₹{item.mrp.toFixed(2)}
                      </p>
                      {item.discounted_price && item.discounted_price < item.mrp ? (
                        <div>
                          <p className={`text-lg font-bold ${item.has_special_price ? 'text-amber-600' : 'text-green-600'}`}>
                            {item.has_special_price ? 'SPECIAL PRICE' : 'GST PAID PRICE'}: ₹{item.discounted_price.toFixed(2)}
                          </p>
                          {item.has_special_price && (
                            <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                              Negotiated Rate
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-lg font-bold text-green-600">
                          Price: ₹{item.mrp.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex flex-col items-end justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFromCart(item.jp_part_no)}
                      data-testid={`remove-item-${item.jp_part_no}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>

                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 rounded-sm"
                          onClick={() => handleQuantityChange(item.jp_part_no, item.quantity - (item.moq || 1), item.moq || 1)}
                          disabled={item.quantity <= (item.moq || 1)}
                          data-testid={`decrease-qty-${item.jp_part_no}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span
                          className="w-12 text-center font-semibold"
                          data-testid={`quantity-${item.jp_part_no}`}
                        >
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 rounded-sm"
                          onClick={() => handleQuantityChange(item.jp_part_no, item.quantity + (item.moq || 1), item.moq || 1)}
                          data-testid={`increase-qty-${item.jp_part_no}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {(item.moq || 1) > 1 && (
                        <p
                          className="text-[10px] text-amber-600 font-medium leading-tight"
                          data-testid={`moq-hint-${item.jp_part_no}`}
                        >
                          MOQ {item.moq} · pack of {item.moq}
                        </p>
                      )}
                    </div>

                    <div className="text-right" data-testid={`subtotal-${item.jp_part_no}`}>
                      {item.discounted_price && item.discounted_price < item.mrp ? (
                        <>
                          <p className="text-xs text-gray-500 line-through">
                            ₹{(item.mrp * item.quantity).toFixed(2)}
                          </p>
                          <p className="text-sm font-semibold text-green-600">
                            ₹{(item.discounted_price * item.quantity).toFixed(2)}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm font-semibold text-gray-900">
                          ₹{(item.mrp * item.quantity).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-4">
            <div className="bg-white border border-gray-300 rounded-sm p-6 lg:sticky lg:top-24" data-testid="order-summary">
              <h2 className="text-xl font-heading font-bold mb-4 text-gray-900">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700 font-medium">Items</span>
                  <span className="font-semibold text-gray-900" data-testid="summary-items-count">
                    {cart.length}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700 font-medium">MRP Total</span>
                  <span className="font-semibold text-gray-900" data-testid="summary-mrp-total">
                    ₹{totalMRP.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-gray-300 pt-3 flex justify-between">
                  <span className="font-bold text-gray-900">Total Amount</span>
                  <span className="font-bold text-xl text-green-600" data-testid="summary-total">
                    ₹{totalDiscounted.toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm h-12 uppercase font-semibold"
                data-testid="proceed-checkout-button"
              >
                Proceed to Checkout
              </Button>

              <Button
                onClick={() => navigate('/catalog')}
                variant="outline"
                className="w-full mt-3 rounded-sm border-gray-300 text-gray-700"
                data-testid="continue-shopping-button"
              >
                Continue Shopping
              </Button>
            </div>

            {/* Mobile sticky bottom action bar — Proceed-to-Checkout always
                reachable without scrolling to bottom of items + summary card.
                Hidden on lg+ (the sticky right-column summary card covers it). */}
            <div
              className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-300 shadow-2xl px-4 py-3 flex items-center gap-3"
              style={{ paddingBottom: 'calc(0.75rem + var(--safe-bottom))' }}
              data-testid="mobile-cart-sticky-bar"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-gray-500 leading-tight">Total ({cart.length} items)</p>
                <p className="text-xl font-bold text-green-600 leading-tight" data-testid="mobile-cart-sticky-total">
                  ₹{totalDiscounted.toFixed(2)}
                </p>
              </div>
              <Button
                onClick={() => navigate('/checkout')}
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-12 px-5 uppercase font-semibold shrink-0"
                data-testid="mobile-cart-sticky-checkout-button"
              >
                Checkout
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
